import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DigitalJourneyRoute } from "app/DigitalJourneyAppFlow/constants";
import { accidentResponsibilityAtom } from "atoms/accidentCircumstance";
import {
  amicableSignedAtom,
  authoritiesInterventionAtom,
  driverPresentAtom,
  informationOnOtherVehicleAtom,
  numInjuredPeopleAtom,
  numVehiclesInvolvedAtom,
} from "atoms/accidentInformation";
import { countryOfAccidentAtom } from "atoms/accidentLocation";
import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { serverSessionAtom } from "atoms/serverSession";
import { sessionIdAtom } from "atoms/sessionId";
import { typeOfClaimAtom } from "atoms/typeOfClaim";
import { vandalismSeverityAtom } from "atoms/vandalismSeverity";
import { vehicleTypeAtom } from "atoms/vehicleType";
import {
  AccidentResponsibility,
  AccidentType,
  AuthoritiesIntervention,
  CaiEnum,
  ClaimHandlingDecision,
  VandalismSeverity,
  VehicleType,
} from "types/DigitalJourneySession";
import { Event } from "types/Event";
import { Session } from "types/Session";
import { replaceSessionInAppRoute } from "utils/routeHelper";

import { useClientTranslation } from "./useClientTranslation";
import { useEventTracking } from "./useEventTracking";

type PageRoutingProps = {
  onNext: () => void;
  nextRoute: DigitalJourneyRoute;
  continueCTA: string;
  onTrackAndNext: (event: Event) => () => void;
  navigateTo: (route: DigitalJourneyRoute) => void;
};

export const useDigitalJourneyPageRouting = (): PageRoutingProps => {
  const navigate = useNavigate();
  const sessionId = useAtomValue(sessionIdAtom);
  const serverSession = useAtomValue(serverSessionAtom);
  const typeOfClaim = useAtomValue(typeOfClaimAtom);
  const informationOnOtherVehicle = useAtomValue(informationOnOtherVehicleAtom);
  const numVehiclesInvolved = useAtomValue(numVehiclesInvolvedAtom);
  const driverPresent = useAtomValue(driverPresentAtom);
  const numInjuredPeople = useAtomValue(numInjuredPeopleAtom);
  const vehicleType = useAtomValue(vehicleTypeAtom);
  const claimHandlingDecision = useAtomValue(claimHandlingDecisionAtom);
  const cai = useAtomValue(amicableSignedAtom);
  const authorities = useAtomValue(authoritiesInterventionAtom);
  const vandalismSeverity = useAtomValue(vandalismSeverityAtom);
  const accidentResponsibility = useAtomValue(accidentResponsibilityAtom);
  const countryOfAccident = useAtomValue(countryOfAccidentAtom);
  const { pathname, state } = useLocation();
  const { trackEvent } = useEventTracking();
  const { t } = useClientTranslation();
  const typesOfPolice = [
    AuthoritiesIntervention.CARABINIERI,
    AuthoritiesIntervention.CITY_POLICE,
    AuthoritiesIntervention.FIREFIGHTERS,
    AuthoritiesIntervention.LOCAL_POLICE,
    AuthoritiesIntervention.OTHER_POLICE,
    AuthoritiesIntervention.POLICE,
  ];

  const getNextDJRoute = (session: Session | null, pathname: string) => {
    if (!session) return pathname;

    switch (pathname) {
      case DigitalJourneyRoute.Landing:
        return DigitalJourneyRoute.Welcome;
      case DigitalJourneyRoute.Welcome:
        return DigitalJourneyRoute.VehicleType;
      case DigitalJourneyRoute.VehicleType:
        return DigitalJourneyRoute.AccidentTimeAndLocation;
      case DigitalJourneyRoute.AccidentTimeAndLocation:
        return DigitalJourneyRoute.TypeOfClaim;
      case DigitalJourneyRoute.TypeOfClaim:
        if (typeOfClaim === AccidentType.COLLISION_BETWEEN_VEHICLES && countryOfAccident !== "Other")
          return DigitalJourneyRoute.AccidentInformation;
        if (typeOfClaim === AccidentType.GLASS) return DigitalJourneyRoute.SelectWindowsDamaged;
        if (typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM) return DigitalJourneyRoute.TypeOfClaimTheftFire;
        if (typeOfClaim === AccidentType.NATURAL_EVENTS) return DigitalJourneyRoute.TypeOfClaimNaturalEvent;
        if (typeOfClaim === AccidentType.KASKO) return DigitalJourneyRoute.TypeOfClaimInsuranceToActivate;
        return DigitalJourneyRoute.CompleteClaimInCustomerPortal;
      case DigitalJourneyRoute.AccidentInformation:
        if (informationOnOtherVehicle && numVehiclesInvolved === 2) return DigitalJourneyRoute.AccidentCircumstance;
        return DigitalJourneyRoute.CompleteClaimInCustomerPortal;
      case DigitalJourneyRoute.AccidentCircumstance:
        return DigitalJourneyRoute.VehicleCondition;
      case DigitalJourneyRoute.VehicleCondition:
        return DigitalJourneyRoute.DamageLocationPH;
      case DigitalJourneyRoute.DamageLocationPH:
        if (
          typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM &&
          vandalismSeverity &&
          (vandalismSeverity === VandalismSeverity.PARTIAL_FIRE ||
            vandalismSeverity === VandalismSeverity.PARTIAL_THEFT ||
            vandalismSeverity === VandalismSeverity.VANDALISM)
        ) {
          if (vehicleType === VehicleType.MOTORCYCLE) return DigitalJourneyRoute.ClaimSummary;
          return DigitalJourneyRoute.HandlingDecision;
        }
        if (typeOfClaim === AccidentType.NATURAL_EVENTS) return DigitalJourneyRoute.AdditionalInformation;
        if (typeOfClaim === AccidentType.KASKO) return DigitalJourneyRoute.PHPersonalDetails;
        if (driverPresent) return DigitalJourneyRoute.PHPersonalDetails;
        return DigitalJourneyRoute.OtherDriverPersonalDetails;
      case DigitalJourneyRoute.PHPersonalDetails:
        if (typeOfClaim === AccidentType.KASKO) return DigitalJourneyRoute.AdditionalInformation;
        return DigitalJourneyRoute.OtherDriverPersonalDetails;
      case DigitalJourneyRoute.OtherDriverPersonalDetails:
        return DigitalJourneyRoute.DamageLocationOther;
      case DigitalJourneyRoute.DamageLocationOther:
        if (numInjuredPeople !== null && numInjuredPeople > 0) return DigitalJourneyRoute.InjuredPeopleDetails;
        if (cai === CaiEnum.ONE_SIGNED || cai === CaiEnum.BOTH_SIGNED) return DigitalJourneyRoute.CAIPhotos;
        if (authorities && typesOfPolice.includes(authorities)) return DigitalJourneyRoute.AuthoritiesDocumentsPhotos;
        return DigitalJourneyRoute.AdditionalDocumentsPhotos;
      case DigitalJourneyRoute.InjuredPeopleDetails:
        if (cai === CaiEnum.ONE_SIGNED || cai === CaiEnum.BOTH_SIGNED) return DigitalJourneyRoute.CAIPhotos;
        if (numInjuredPeople !== null && numInjuredPeople > 0) return DigitalJourneyRoute.InjuredPeopleDocumentPhotos;
        if (authorities && typesOfPolice.includes(authorities)) return DigitalJourneyRoute.AuthoritiesDocumentsPhotos;
        return DigitalJourneyRoute.AdditionalDocumentsPhotos;
      case DigitalJourneyRoute.CAIPhotos:
        if (numInjuredPeople !== null && numInjuredPeople > 0) return DigitalJourneyRoute.InjuredPeopleDocumentPhotos;
        if (authorities && typesOfPolice.includes(authorities)) return DigitalJourneyRoute.AuthoritiesDocumentsPhotos;
        return DigitalJourneyRoute.AdditionalDocumentsPhotos;
      case DigitalJourneyRoute.InjuredPeopleDocumentPhotos:
        if (authorities && typesOfPolice.includes(authorities)) return DigitalJourneyRoute.AuthoritiesDocumentsPhotos;
        return DigitalJourneyRoute.AdditionalDocumentsPhotos;
      case DigitalJourneyRoute.AuthoritiesDocumentsPhotos:
        if (
          typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM &&
          vandalismSeverity &&
          (vandalismSeverity === VandalismSeverity.PARTIAL_FIRE ||
            vandalismSeverity === VandalismSeverity.PARTIAL_THEFT ||
            vandalismSeverity === VandalismSeverity.VANDALISM)
        )
          return DigitalJourneyRoute.VehicleCondition;
        if (
          typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM &&
          vandalismSeverity &&
          (vandalismSeverity === VandalismSeverity.TOTAL_FIRE || vandalismSeverity === VandalismSeverity.TOTAL_THEFT)
        )
          return DigitalJourneyRoute.ClaimSummary;
        return DigitalJourneyRoute.AdditionalDocumentsPhotos;
      case DigitalJourneyRoute.AdditionalDocumentsPhotos:
        if (typeOfClaim === AccidentType.GLASS || typeOfClaim === AccidentType.NATURAL_EVENTS)
          return DigitalJourneyRoute.ClaimSummary;
        if (typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM && authorities && typesOfPolice.includes(authorities))
          return DigitalJourneyRoute.AuthoritiesDocumentsPhotos;
        if (typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM && authorities && !typesOfPolice.includes(authorities)) {
          if (
            typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM &&
            vandalismSeverity &&
            (vandalismSeverity === VandalismSeverity.PARTIAL_FIRE ||
              vandalismSeverity === VandalismSeverity.PARTIAL_THEFT ||
              vandalismSeverity === VandalismSeverity.VANDALISM)
          )
            return DigitalJourneyRoute.VehicleCondition;
          if (
            typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM &&
            vandalismSeverity &&
            (vandalismSeverity === VandalismSeverity.TOTAL_FIRE || vandalismSeverity === VandalismSeverity.TOTAL_THEFT)
          )
            return DigitalJourneyRoute.ClaimSummary;
        }
        if (typeOfClaim === AccidentType.KASKO) return DigitalJourneyRoute.HandlingDecision;
        return DigitalJourneyRoute.AdditionalInformation;
      case DigitalJourneyRoute.SelectWindowsDamaged:
        return DigitalJourneyRoute.AdditionalInformation;
      case DigitalJourneyRoute.TypeOfClaimTheftFire:
        return DigitalJourneyRoute.AdditionalInformation;
      case DigitalJourneyRoute.TypeOfClaimInsuranceToActivate:
        return DigitalJourneyRoute.VehicleCondition;
      case DigitalJourneyRoute.TypeOfClaimNaturalEvent:
        return DigitalJourneyRoute.DamageLocationPH;
      case DigitalJourneyRoute.AdditionalInformation:
        if (vehicleType === VehicleType.MOTORCYCLE || accidentResponsibility === AccidentResponsibility.T)
          return DigitalJourneyRoute.ClaimSummary;
        if (
          typeOfClaim === AccidentType.GLASS ||
          typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM ||
          typeOfClaim === AccidentType.NATURAL_EVENTS ||
          typeOfClaim === AccidentType.KASKO
        )
          return DigitalJourneyRoute.AdditionalDocumentsPhotos;
        return DigitalJourneyRoute.HandlingDecision;
      case DigitalJourneyRoute.HandlingDecision:
        if (claimHandlingDecision === ClaimHandlingDecision.BODY_SHOP) return DigitalJourneyRoute.SelectBodyShop;
        if (claimHandlingDecision === ClaimHandlingDecision.PHOTO_CAPTURE)
          return DigitalJourneyRoute.DamagePhotoCapture;
        return DigitalJourneyRoute.ClaimSummary;
      case DigitalJourneyRoute.SelectBodyShop:
        return DigitalJourneyRoute.ClaimSummary;
      case DigitalJourneyRoute.DamagePhotoCapture:
        return DigitalJourneyRoute.VehiclePhotoCapture;
      case DigitalJourneyRoute.VehiclePhotoCapture:
        return DigitalJourneyRoute.ClaimSummary;
      case DigitalJourneyRoute.ClaimSummary:
        return DigitalJourneyRoute.FlowCompleted;
      default:
        return pathname;
    }
  };

  // This gets the route from /sessionid/path -> path
  const route = pathname.replace(sessionId, ":session");
  const matchingNextRoute = getNextDJRoute(serverSession, route) as DigitalJourneyRoute;
  const nextRoute = replaceSessionInAppRoute(matchingNextRoute, sessionId) as DigitalJourneyRoute;
  const continueCTA = t("global.continue");

  const onNext = useCallback(() => {
    navigate(nextRoute);
  }, [navigate, sessionId, nextRoute, state]);

  const onTrackAndNext = (event: Event) => () => {
    trackEvent(event);
    onNext();
  };

  const navigateTo = (route: DigitalJourneyRoute) => {
    navigate(replaceSessionInAppRoute(route, sessionId));
  };

  useEffect(() => {
    if (serverSession && serverSession.completedAt) {
      navigate(replaceSessionInAppRoute(DigitalJourneyRoute.FlowCompleted, sessionId));
    }
  }, [navigate, route, sessionId]);

  return {
    onNext,
    nextRoute,
    continueCTA,
    onTrackAndNext,
    navigateTo,
  };
};
